import { useEffect, useRef, useState } from "react";

import classNames from "classnames/bind";
import UIIcon from "common/blocks/UIIcon";
import useMediaQuery from "hooks/useMediaQuery/useMediaQuery";
import parse from "html-react-parser";
import type { ResolvedCustomTable } from "resolvers/data/customTableResolver";
import { CustomTableHeaderDirection } from "resolvers/data/customTableResolver";
import { scrollBy } from "seamless-scroll-polyfill";

import styles from "./Table.module.scss";

const cx = classNames.bind(styles);

const DEFAULT_CHEVRON_ICONS_VALUE: ChevronIcons = {
    isShownChevronLeft: false,
    isShownChevronRight: false,
};

enum ScrollDirection {
    Left = "left",
    Right = "right",
}

interface ChevronIcons {
    isShownChevronLeft: boolean;
    isShownChevronRight: boolean;
}

export type TableProps = Omit<ResolvedCustomTable, "codename" | "type">;

const Table = (props: TableProps) => {
    const { headerDirection, table } = props;

    const { isAboveDesktopLarge } = useMediaQuery();
    const tableContainerRef = useRef<HTMLDivElement>(null);
    const [chevronIcons, setChevronIcons] = useState<ChevronIcons>(DEFAULT_CHEVRON_ICONS_VALUE);

    useEffect(() => {
        const tableRef = tableContainerRef.current;

        const handleScrollEvent = () => {
            toggleChevronIcons();
        };

        if (tableRef) {
            toggleChevronIcons();

            tableRef.addEventListener("scroll", handleScrollEvent);
            window.addEventListener("resize", handleScrollEvent);
        }

        return () => {
            if (tableRef) {
                tableRef.removeEventListener("scroll", handleScrollEvent);
                window.removeEventListener("resize", handleScrollEvent);
            }
        };
    }, []);

    const toggleChevronIcons = () => {
        if (!tableContainerRef.current) {
            return;
        }

        const scrollableTableItem = tableContainerRef.current.children[0] as HTMLElement;
        const scrollableTableWidth = scrollableTableItem.offsetWidth;
        const offset = tableContainerRef.current.scrollLeft;

        const tableContainerStyles = getComputedStyle(tableContainerRef.current);
        const tableContainerWidth = +tableContainerStyles.width.replace("px", "");

        if (tableContainerWidth < scrollableTableWidth) {
            setChevronIcons({
                isShownChevronLeft: offset > 0,
                isShownChevronRight: scrollableTableWidth - tableContainerWidth > offset,
            });
        } else {
            setChevronIcons(DEFAULT_CHEVRON_ICONS_VALUE);
        }
    };

    const clickToScroll = (scrollDirection: ScrollDirection) => {
        if (tableContainerRef.current) {
            scrollBy(tableContainerRef.current, {
                left: scrollDirection === ScrollDirection.Right ? 150 : -150,
                behavior: "smooth",
            });
        }
    };

    const handleClickOnChevronIcon = (scrollDirection: ScrollDirection) => {
        if (tableContainerRef.current) {
            toggleChevronIcons();
            clickToScroll(scrollDirection);
        }
    };

    const { isShownChevronLeft, isShownChevronRight } = chevronIcons;

    const parsedTable = parse(table);
    const isLeftHeader = headerDirection === CustomTableHeaderDirection.Left;
    const isTopHeader = headerDirection === CustomTableHeaderDirection.Top;
    const isTopLeftHeader = headerDirection === CustomTableHeaderDirection.TopLeft;

    return (
        <div
            className={cx("tableWrapper", {
                tableWrapperDesktop: isAboveDesktopLarge,
            })}
        >
            {!isAboveDesktopLarge && (
                <>
                    <div
                        className={cx("iconWrapper", "iconWrapperLeft", {
                            iconWrapperLeftHide: !isShownChevronLeft,
                        })}
                        onClick={() => handleClickOnChevronIcon(ScrollDirection.Left)}
                    >
                        <UIIcon icon="ChevronLeft" className={cx("icon")} />
                    </div>
                    <div
                        className={cx("iconWrapper", "iconWrapperRight", {
                            iconWrapperRightHide: !isShownChevronRight,
                        })}
                        onClick={() => handleClickOnChevronIcon(ScrollDirection.Right)}
                    >
                        <UIIcon icon="ChevronRight" className={cx("icon")} />
                    </div>
                </>
            )}
            <div
                ref={tableContainerRef}
                className={cx("tableContainer", {
                    tableContainerTopHeader: isTopHeader || isTopLeftHeader,
                    tableContainerLeftHeader: isLeftHeader || isTopLeftHeader,
                })}
            >
                {parsedTable}
            </div>
        </div>
    );
};

export default Table;
